import React, { useEffect, useState, useContext, forwardRef } from 'react';
import { node, string, bool } from 'prop-types';
import cn from 'clsx';
import ModalContext from '../../context/ModalContext.js';

/**
 * Modal Body component.
 */
const Body = /*#__PURE__*/forwardRef(({
  children,
  className,
  maxHeight,
  noScroll
}, ref) => {
  const transitionSpeed = 200;
  const [enableBodyTransition, setEnableBodyTransition] = useState(false);
  const {
    show,
    fluid
  } = useContext(ModalContext);

  // Enable body transition only after the modal is first mounted
  // to prevent any weirdness when the modal first appears. The 0 timeout adds
  // the function to the message queue to execute immediately after first mount.
  useEffect(() => {
    let timer;
    if (show) {
      timer = setTimeout(() => setEnableBodyTransition(true), 0);
    } else {
      setEnableBodyTransition(false);
    }
    return () => {
      clearTimeout(timer);
      setEnableBodyTransition(false);
    };
  }, [show]);
  return /*#__PURE__*/React.createElement("div", {
    className: cn('modal__body', className),
    ref: ref,
    style: {
      maxHeight: fluid ? null : maxHeight,
      transition: enableBodyTransition ? `max-height ${transitionSpeed}ms` : null,
      overflowY: noScroll ? 'hidden' : 'auto'
    }
  }, children);
});
Body.displayName = 'Modal.Body';
Body.defaultProps = {
  children: null,
  className: null,
  maxHeight: '377px',
  noScroll: false
};
Body.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The content of the body */
  children: node,
  /** Additional class names to add to the component root. */
  className: string,
  /** Maximum height of the modal, which will transition when updated. */
  maxHeight: string,
  /** Prevent the modal body from scrolling. */
  noScroll: bool
} : {};
export default Body;